import { readRequired } from '@lib/customization/poiDesign';
import { useLocalization } from '@lib/localization';
import { Box, Switch, Typography } from '@mui/material';
import { type PoiField } from '@store/api/poiTypeEndpoints';
import { AngleDownIcon, BarcodeIcon } from 'react-line-awesome';
import { fakeInputStyles } from './designer.styles';
import FieldHeading, { LabelText, type Languages } from './FieldHeading';

interface Props {
  field: PoiField;
  languages: Languages;
  /** The designer's actions for this field, kept at the end of its label row. */
  actions?: React.ReactNode;
}

/**
 * A field on the design canvas: the device's control drawn without its border, showing the
 * author's default, taking no input. The canvas holds many of these, and outlines around
 * every one of them read as noise.
 */
const FieldSketch: React.FC<Props> = ({ field, languages, actions }) => {
  const { t } = useLocalization();

  const { value } = field;
  const choice = (id: string) => (
    <LabelText key={id} text={field.options?.find((option) => option.id === id)?.label} empty="…" languages={languages} />
  );
  const hint = (text: string) => <Box component="span" sx={{ color: 'text.disabled' }}>{field.placeholder || text}</Box>;

  const box = (content: React.ReactNode, end?: React.ReactNode, tall = false) => (
    <Box sx={{ ...fakeInputStyles, ...(tall && { minHeight: 56, alignItems: 'flex-start', pt: 0.75 }) }}>
      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{content}</Box>
      {end && <Box sx={{ display: 'inline-flex', color: 'text.disabled' }}>{end}</Box>}
    </Box>
  );

  const control = () => {
    switch (field.type) {
      case 'Range': {
        const min = field.min ?? 0;
        const max = field.max ?? 100;
        const at = typeof value === 'number' && max > min ? ((value - min) / (max - min)) * 100 : 0;

        return (
          <Box sx={{ position: 'relative', height: 4, my: 1.25, borderRadius: 2, backgroundColor: 'action.selected' }}>
            <Box sx={{ position: 'absolute', inset: 0, width: `${at}%`, borderRadius: 2, backgroundColor: 'primary.main' }} />
          </Box>
        );
      }
      case 'List':
        return box(typeof value === 'string' && value ? choice(value) : hint(t('Customization_Designer_Select')), <AngleDownIcon />);
      case 'MultiList':
        return box(Array.isArray(value) && value.length
          ? value.map((id, index) => <span key={id}>{index ? ', ' : ''}{choice(id)}</span>)
          : hint(t('Customization_Designer_SelectSeveral')), <AngleDownIcon />);
      case 'Barcode':
        return box(typeof value === 'string' && value ? value : hint(t('Customization_Designer_Scan')), <BarcodeIcon />);
      case 'Number':
        return box(typeof value === 'number' ? value : hint(''));
      default:
        return box(typeof value === 'string' && value ? value : hint(''), undefined, Boolean(field.multiline));
    }
  };

  const end = field.type === 'Toggle'
    ? <Switch size="small" checked={Boolean(value)} sx={{ pointerEvents: 'none' }} />
    : field.type === 'Range' && <Typography variant="caption" color="text.secondary">{`${field.min ?? 0} – ${field.max ?? 100}`}</Typography>;

  return (
    <Box>
      <FieldHeading
        field={field} languages={languages} required={readRequired(field)} readOnly={field.readOnly}
        trailing={<>{actions}{end}</>}
      />
      {field.type !== 'Toggle' && <Box sx={{ mt: 0.25 }}>{control()}</Box>}
      {field.help && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{field.help}</Typography>}
    </Box>
  );
};

export default FieldSketch;
